import { useEffect, useRef } from 'react';

type UseInfiniteScrollProps = {
  infiniteScroll?: boolean;
  hasMoreItems?: boolean;
  loadNextPage?: () => void;
  threshold?: number;
};

const useInfiniteScroll = <T extends HTMLElement>({
  infiniteScroll,
  hasMoreItems,
  loadNextPage,
  threshold = 1,
}: UseInfiniteScrollProps) => {
  const infiniteScrollObserverTarget = useRef<T | null>(null);

  useEffect(() => {
    if (infiniteScroll) {
      const observer = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting) {
            hasMoreItems && loadNextPage && loadNextPage();
          }
        },
        { threshold },
      );

      if (infiniteScrollObserverTarget.current) {
        observer.observe(infiniteScrollObserverTarget.current);
      }

      return () => {
        if (infiniteScrollObserverTarget.current) {
          observer.unobserve(infiniteScrollObserverTarget.current);
        }
      };
    }
  }, [infiniteScroll, threshold, loadNextPage]);

  return infiniteScrollObserverTarget;
};

export default useInfiniteScroll;
